import { Navigate, useRoutes } from 'react-router-dom';
// auth
import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
// layouts
import CompactLayout from '../layouts/compact';
import DashboardLayout from '../layouts/dashboard';
import SimpleLayout from '../layouts/simple';
// config
import { PATH_AFTER_LOGIN } from '../config-global';
//
import {
  //
  BlankPage,
  BlogNewPostPage,
  BlogPostPage,
  // Dashboard: Blog
  BlogPostsPage,
  // Dashboard: Brand
  BrandCreatePage,
  BrandEditPage,
  BrandItemsListPage,
  BrandListPage,
  BrandPhotosListPage,
  // Dashboard: Calendar
  CalendarPage,
  // Dashboard: App
  ChatPage,
  ComingSoonPage,
  // Dashboard: Contact
  ContactInfoEditPage,
  // Coupon
  CouponListPage,
  CouponsStatsPage,
  EcommerceCheckoutPage,
  EcommerceProductCreatePage,
  EcommerceProductDetailsPage,
  EcommerceProductEditPage,
  EcommerceProductListPage,
  // Dashboard: Ecommerce
  EcommerceShopPage,
  EventCalenderPage,
  EventCancelledReservationsPage,
  EventCreatePage,
  EventDayDetailsPage,
  EventDeletedReservationsPage,
  EventDetailsPage,
  EventEditPage,
  EventExtendPage,
  // Dashboard: Event
  EventListPage,
  EventReservationsPage,
  // Dashboard: FileManager
  FileManagerPage,
  GeneralAnalyticsPage,
  // Dashboard: General
  GeneralAppPage,
  GeneralBankingPage,
  GeneralBookingPage,
  GeneralEcommercePage,
  GeneralFilePage,
  // HdykuListPage
  HdykuListPage,
  InvoiceCreatePage,
  InvoiceDetailsPage,
  InvoiceEditPage,
  // Dashboard: Invoice
  InvoiceListPage,
  KanbanPage,
  // Auth
  LoginPage,
  MailPage,
  MaintenancePage,
  MangePage,
  // Dashboard: Message
  MessageListPage,
  // Dashboard: Neighborhoods
  NeighborhoodListPage,
  NewPasswordPage,
  Page403,
  Page404,
  //
  Page500,
  // Partner
  PartnerCreatePage,
  PartnerEditPage,
  PartnerListPage,
  PaymentPage,
  PermissionDeniedPage,
  PricingPage,
  RegisterPage,
  // Dashboard: Reservation
  ReservationCreatePage,
  ResetPasswordPage,
  // Service
  ServiceCreatePage,
  ServiceEditPage,
  ServiceListPage,
  // Dashboard: User
  UserAccountPage,
  UserCardsPage,
  UserCreatePage,
  UserEditPage,
  // Dashboard: User
  UserListPage,
  UserProfilePage,
  VerifyCodePage,
  // Dashboard: Performance
  PerformanceListPage,
  PerformanceCreatePage,
  PerformanceEditPage,
  PolicyPage,
  PolicyUpdatePage,
  PolicyCreatePage,
} from './elements';

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    // Auth
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <LoginPage /> },
        { path: 'register-unprotected', element: <RegisterPage /> },
        {
          element: <CompactLayout />,
          children: [
            { path: 'reset-password', element: <ResetPasswordPage /> },
            { path: 'new-password', element: <NewPasswordPage /> },
            { path: 'verify', element: <VerifyCodePage /> },
          ],
        },
      ],
    },

    // Dashboard
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <GeneralAppPage /> },
        { path: 'ecommerce', element: <GeneralEcommercePage /> },
        { path: 'analytics', element: <GeneralAnalyticsPage /> },
        { path: 'banking', element: <GeneralBankingPage /> },
        { path: 'booking', element: <GeneralBookingPage /> },
        { path: 'file', element: <GeneralFilePage /> },
        {
          path: 'e-commerce',
          children: [
            {
              element: <Navigate to="/dashboard/e-commerce/shop" replace />,
              index: true,
            },
            { path: 'shop', element: <EcommerceShopPage /> },
            { path: 'product/:name', element: <EcommerceProductDetailsPage /> },
            { path: 'list', element: <EcommerceProductListPage /> },
            { path: 'product/new', element: <EcommerceProductCreatePage /> },
            {
              path: 'product/:name/edit',
              element: <EcommerceProductEditPage />,
            },
            { path: 'checkout', element: <EcommerceCheckoutPage /> },
          ],
        },
        {
          path: 'user',
          children: [
            {
              element: <Navigate to="/dashboard/user/profile" replace />,
              index: true,
            },
            { path: 'profile', element: <UserProfilePage /> },
            { path: 'cards', element: <UserCardsPage /> },
            { path: 'list', element: <UserListPage /> },
            { path: 'new', element: <UserCreatePage /> },
            { path: ':id/edit', element: <UserEditPage /> },
            { path: 'account', element: <UserAccountPage /> },
          ],
        },
        {
          path: 'policy',
          children: [
            { path: '/dashboard/policy', element: <PolicyPage /> },
            { path: '/dashboard/policy/create', element: <PolicyCreatePage /> },
            { path: '/dashboard/policy/update/:id', element: <PolicyUpdatePage /> },
          ],
        },
        {
          path: 'service',
          children: [
            {
              element: <Navigate to="/dashboard/service/list" replace />,
              index: true,
            },
            { path: 'list', element: <ServiceListPage /> },
            { path: 'new', element: <ServiceCreatePage /> },
            { path: ':id/edit', element: <ServiceEditPage /> },
          ],
        },
        {
          path: 'partner',
          children: [
            {
              element: <Navigate to="/dashboard/partner/list" replace />,
              index: true,
            },
            { path: 'list', element: <PartnerListPage /> },
            { path: 'new', element: <PartnerCreatePage /> },
            { path: ':id/edit', element: <PartnerEditPage /> },
          ],
        },
        {
          path: 'message',
          children: [
            {
              element: <Navigate to="/dashboard/message/list" replace />,
              index: true,
            },
            { path: 'list', element: <MessageListPage /> },
          ],
        },
        {
          path: 'contact',
          children: [
            {
              element: <Navigate to="/dashboard/contact/info" replace />,
              index: true,
            },
            { path: 'info', element: <ContactInfoEditPage /> },
          ],
        },
        {
          path: 'brand',
          children: [
            {
              element: <Navigate to="/dashboard/brand/list" replace />,
              index: true,
            },
            { path: 'list', element: <BrandListPage /> },
            { path: 'new', element: <BrandCreatePage /> },
            { path: ':id/edit', element: <BrandEditPage /> },
            { path: ':id/items', element: <BrandItemsListPage /> },
            { path: ':id/photos', element: <BrandPhotosListPage /> },
          ],
        },
        {
          path: 'invoice',
          children: [
            {
              element: <Navigate to="/dashboard/invoice/list" replace />,
              index: true,
            },
            { path: 'list', element: <InvoiceListPage /> },
            { path: ':id', element: <InvoiceDetailsPage /> },
            { path: ':id/edit', element: <InvoiceEditPage /> },
            { path: 'new', element: <InvoiceCreatePage /> },
          ],
        },
        {
          path: 'blog',
          children: [
            {
              element: <Navigate to="/dashboard/blog/posts" replace />,
              index: true,
            },
            { path: 'posts', element: <BlogPostsPage /> },
            { path: 'post/:id', element: <BlogPostPage /> },
            { path: 'new', element: <BlogNewPostPage /> },
          ],
        },
        {
          path: 'event',
          children: [
            {
              element: <Navigate to="/dashboard/event/list" replace />,
              index: true,
            },
            { path: 'list', element: <EventListPage /> },
            { path: 'new', element: <EventCreatePage /> },
            { path: ':id/edit', element: <EventEditPage /> },
            { path: ':id/extend', element: <EventExtendPage /> },
            { path: ':id', element: <EventDetailsPage /> },
            { path: ':id/day/:dayId', element: <EventDayDetailsPage /> },
            { path: ':id/reservations/:stateType', element: <EventReservationsPage /> },
            {
              path: ':id/deleted-reservations',
              element: <EventDeletedReservationsPage />,
            },
            {
              path: ':id/cancelled-reservations',
              element: <EventCancelledReservationsPage />,
            },
            { path: ':id/calender', element: <EventCalenderPage /> },
          ],
        },
        {
          path: 'neighborhood',
          children: [
            {
              element: <Navigate to="/dashboard/neighborhood/list" replace />,
              index: true,
            },
            { path: 'list', element: <NeighborhoodListPage /> },
          ],
        },
        {
          path: 'hdyku',
          children: [
            {
              element: <Navigate to="/dashboard/hdyku/list" replace />,
              index: true,
            },
            { path: 'list', element: <HdykuListPage /> },
          ],
        },
        {
          path: 'coupon',
          children: [
            { element: <Navigate to="/dashboard/coupon" replace /> },
            { path: 'list', element: <CouponListPage /> },
            { path: 'stats', element: <CouponsStatsPage /> },
          ],
        },
        {
          path: 'reservation',
          children: [
            {
              element: <Navigate to="/dashboard/reservation/new" replace />,
              index: true,
            },
            { path: 'new', element: <ReservationCreatePage /> },
          ],
        },
        {
          path: 'mange',
          children: [{ path: '/dashboard/mange', element: <MangePage /> }],
        },
        {
          path: 'performance',
          children: [
            {
              element: <Navigate to="/dashboard/performance/list" replace />,
              index: true,
            },
            { path: 'list', element: <PerformanceListPage /> },
            { path: 'new', element: <PerformanceCreatePage /> },
            { path: ':id/edit', element: <PerformanceEditPage /> },
          ],
        },
        { path: 'files-manager', element: <FileManagerPage /> },
        {
          path: 'mail',
          children: [
            {
              element: <Navigate to="/dashboard/mail/all" replace />,
              index: true,
            },
            { path: 'label/:customLabel', element: <MailPage /> },
            { path: 'label/:customLabel/:mailId', element: <MailPage /> },
            { path: ':systemLabel', element: <MailPage /> },
            { path: ':systemLabel/:mailId', element: <MailPage /> },
          ],
        },
        {
          path: 'chat',
          children: [
            { element: <ChatPage />, index: true },
            { path: 'new', element: <ChatPage /> },
            { path: ':conversationKey', element: <ChatPage /> },
          ],
        },
        // { path: 'calendar', element: <CalendarPage /> },
        // { path: 'kanban', element: <KanbanPage /> },
        // { path: 'permission-denied', element: <PermissionDeniedPage /> },
        // { path: 'blank', element: <BlankPage /> },
      ],
    },

    // Main Routes
    // {
    //   element: <MainLayout />,
    //   children: [
    //     { element: <HomePage />, index: true },
    //     { path: 'about-us', element: <AboutPage /> },
    //     { path: 'contact-us', element: <Contact /> },
    //     { path: 'faqs', element: <FaqsPage /> },
    //     // Demo Components
    //     {
    //       path: 'components',
    //       children: [
    //         { element: <ComponentsOverviewPage />, index: true },
    //         {
    //           path: 'foundation',
    //           children: [
    //             { element: <Navigate to="/components/foundation/colors" replace />, index: true },
    //             { path: 'colors', element: <FoundationColorsPage /> },
    //             { path: 'typography', element: <FoundationTypographyPage /> },
    //             { path: 'shadows', element: <FoundationShadowsPage /> },
    //             { path: 'grid', element: <FoundationGridPage /> },
    //             { path: 'icons', element: <FoundationIconsPage /> },
    //           ],
    //         },
    //         {
    //           path: 'mui',
    //           children: [
    //             { element: <Navigate to="/components/mui/accordion" replace />, index: true },
    //             { path: 'accordion', element: <MUIAccordionPage /> },
    //             { path: 'alert', element: <MUIAlertPage /> },
    //             { path: 'autocomplete', element: <MUIAutocompletePage /> },
    //             { path: 'avatar', element: <MUIAvatarPage /> },
    //             { path: 'badge', element: <MUIBadgePage /> },
    //             { path: 'breadcrumbs', element: <MUIBreadcrumbsPage /> },
    //             { path: 'buttons', element: <MUIButtonsPage /> },
    //             { path: 'checkbox', element: <MUICheckboxPage /> },
    //             { path: 'chip', element: <MUIChipPage /> },
    //             { path: 'data-grid', element: <MUIDataGridPage /> },
    //             { path: 'dialog', element: <MUIDialogPage /> },
    //             { path: 'list', element: <MUIListPage /> },
    //             { path: 'menu', element: <MUIMenuPage /> },
    //             { path: 'pagination', element: <MUIPaginationPage /> },
    //             { path: 'pickers', element: <MUIPickersPage /> },
    //             { path: 'popover', element: <MUIPopoverPage /> },
    //             { path: 'progress', element: <MUIProgressPage /> },
    //             { path: 'radio-button', element: <MUIRadioButtonsPage /> },
    //             { path: 'rating', element: <MUIRatingPage /> },
    //             { path: 'slider', element: <MUISliderPage /> },
    //             { path: 'stepper', element: <MUIStepperPage /> },
    //             { path: 'switch', element: <MUISwitchPage /> },
    //             { path: 'table', element: <MUITablePage /> },
    //             { path: 'tabs', element: <MUITabsPage /> },
    //             { path: 'textfield', element: <MUITextFieldPage /> },
    //             { path: 'timeline', element: <MUITimelinePage /> },
    //             { path: 'tooltip', element: <MUITooltipPage /> },
    //             { path: 'transfer-list', element: <MUITransferListPage /> },
    //             { path: 'tree-view', element: <MUITreesViewPage /> },
    //           ],
    //         },
    //         {
    //           path: 'extra',
    //           children: [
    //             { element: <Navigate to="/components/extra/animate" replace />, index: true },
    //             { path: 'animate', element: <DemoAnimatePage /> },
    //             { path: 'carousel', element: <DemoCarouselsPage /> },
    //             { path: 'chart', element: <DemoChartsPage /> },
    //             { path: 'copy-to-clipboard', element: <DemoCopyToClipboardPage /> },
    //             { path: 'editor', element: <DemoEditorPage /> },
    //             { path: 'form-validation', element: <DemoFormValidationPage /> },
    //             { path: 'image', element: <DemoImagePage /> },
    //             { path: 'label', element: <DemoLabelPage /> },
    //             { path: 'lightbox', element: <DemoLightboxPage /> },
    //             { path: 'map', element: <DemoMapPage /> },
    //             { path: 'mega-menu', element: <DemoMegaMenuPage /> },
    //             { path: 'multi-language', element: <DemoMultiLanguagePage /> },
    //             { path: 'navigation-bar', element: <DemoNavigationBarPage /> },
    //             { path: 'organization-chart', element: <DemoOrganizationalChartPage /> },
    //             { path: 'scroll', element: <DemoScrollbarPage /> },
    //             { path: 'snackbar', element: <DemoSnackbarPage /> },
    //             { path: 'text-max-line', element: <DemoTextMaxLinePage /> },
    //             { path: 'upload', element: <DemoUploadPage /> },
    //             { path: 'markdown', element: <DemoMarkdownPage /> },
    //           ],
    //         },
    //       ],
    //     },
    //   ],
    // },
    {
      // path: '/',
      // element: <MainLayout />,
      children: [
        {
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
          index: true,
        },
      ],
    },
    {
      element: <SimpleLayout />,
      children: [
        { path: 'pricing', element: <PricingPage /> },
        { path: 'payment', element: <PaymentPage /> },
      ],
    },
    {
      element: <CompactLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoonPage /> },
        { path: 'maintenance', element: <MaintenancePage /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
